import { useMemo, useState } from 'react';
import { useEmbeddableChants } from '../../lib/chants';

/**
 * Which chant a Library document points at.
 *
 * The author was typing `/chants/purusha-suktam.json` by hand, which meant
 * their own chants were unreachable in practice — nothing on the page told
 * them the path a published document lands on.
 *
 * Two sources, one list, because the author does not care where a chant lives:
 * the eleven shipped texts, and their own (and shared) documents once
 * PUBLISHED. Both resolve to `/chants/<slug>.json` — `POST /publish` writes to
 * exactly the path the shipped ones use — so the block's data shape does not
 * change and every document already in the Library keeps working.
 *
 * An unpublished chant is deliberately absent, and the picker says why: the end
 * user only ever sees what a Library document points at, so embedding a draft
 * would put an unfinished text in front of every reader of the page.
 *
 * The URL field stays. A picker that removes the ability to type a path takes
 * something away.
 */
export function ChantPicker({
  value,
  onPick,
}: {
  value: string;
  onPick: (src: string, title: string) => void;
}) {
  const { chants, isLoading } = useEmbeddableChants();
  const [q, setQ] = useState('');

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (needle === '') return chants;
    return chants.filter(
      (c) => c.title.toLowerCase().includes(needle) || c.slug.includes(needle),
    );
  }, [chants, q]);

  const srcOf = (slug: string): string => `/chants/${slug}.json`;
  const mine = chants.filter((c) => c.origin === 'mine').length;

  return (
    <div className="chant-picker">
      <input
        className="auth-input"
        placeholder="Search your chants and the shipped texts…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="chant-picker__list">
        {isLoading && <p className="auth-hint">Loading your chants…</p>}
        {rows.map((c) => {
          const src = srcOf(c.slug);
          return (
            <button
              key={c.slug}
              type="button"
              className={`chant-picker__row${src === value ? ' is-on' : ''}`}
              onClick={() => onPick(src, c.title)}
            >
              <span className="chant-picker__t">{c.title}</span>
              {c.verses !== undefined && (
                <span className="chant-picker__n">{c.verses} verses</span>
              )}
              <span className="chant-picker__o">{c.origin === 'mine' ? 'yours' : 'shipped'}</span>
            </button>
          );
        })}
        {!isLoading && rows.length === 0 && (
          <p className="auth-hint">Nothing matches “{q}”.</p>
        )}
      </div>
      {!isLoading && mine === 0 && (
        <p className="auth-hint">
          Only the shipped texts are listed. Your own chants appear here once you
          <strong> publish</strong> them in the chant editor — a draft is not offered,
          because a reader of this page would see it.
        </p>
      )}
    </div>
  );
}
